import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { createTenantLead } from '../../services/crmService'; 
import { collection, query, where, getDocs } from 'firebase/firestore'; 
import { db } from '../../services/firebase'; 

// DYNAMIC SERVICE MATRIX 
// Add new services here in the future and the form will automatically update
const SERVICE_OPTIONS = [
  { id: 'eekoCrm', label: 'eeKoSystem CRM' },
  { id: 'dirtToTurnkey', label: 'Dirt-To-Turnkey Deployments' },
  { id: 'bespokeInfra', label: 'Bespoke Digital Infrastructure' },
  { id: 'performanceConsulting', label: 'Performance & Sales Consulting' },
  { id: 'uiComponents', label: 'Customer UI Components & Widgets' },
  { id: 'decoupledWeb', label: 'De-Coupled Web Design' },
  { id: 'customSpa', label: 'Custom Single Page Applications' },
  { id: 'platformIntegration', label: 'Platform Deployment & Integration' },
  { id: 'customPos', label: 'Custom POS Services' },
  { id: 'other', label: 'Other / Custom Request' }
];

export default function LeadIntakeForm({ onClose, isOperatorView = false }) {
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Founder Routing State
  const [contractors, setContractors] = useState([]);
  const [assignedRep, setAssignedRep] = useState('');

  const [formData, setFormData] = useState({
    entityName: '',
    entityRole: '', 
    industry: '',
    employeeCount: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    targetMrr: '',
    // Dynamically generate the initial state based on the array above
    assignedApps: SERVICE_OPTIONS.reduce((acc, service) => {
      acc[service.id] = false;
      return acc;
    }, {}),
    notes: ''
  });

  // If a Founder is opening this (Route Lead), fetch the fleet so they can assign the lead
  useEffect(() => {
    if (!isOperatorView && tenantId && !tenantLoading) {
      const fetchFleet = async () => {
        try {
          const usersRef = collection(db, 'users');
          const q = query(
            usersRef,
            where('tenant_id', '==', tenantId),
            where('accountType', 'in', ['CONTRACTOR', 'INTERNAL', 'SYS_FOUNDER', 'MASTER_AGENT', 'TENANT_ADMIN'])
          );
          
          const snapshot = await getDocs(q);
          const fleet = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          setContractors(fleet);
          if (fleet.length > 0) setAssignedRep(fleet[0].id); // Default to first rep
        } catch (error) {
          console.error("Failed to fetch fleet for routing:", error);
        }
      };
      fetchFleet();
    }
  }, [isOperatorView, tenantId, tenantLoading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        assignedApps: { ...formData.assignedApps, [name]: checked }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenantId) {
      alert("System Error: Corporate identity not resolved. Cannot process lead.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      let targetUid;
      let targetName;

      // STRICT ROUTING DEFINITIONS
      if (isOperatorView) {
        // Quick Lead: Auto-assign to the logged-in user
        targetUid = currentUser?.uid;
        targetName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown Operator';
      } else {
        // Route Lead: Assign to the selected fleet member
        const selectedContractor = contractors.find(c => c.id === assignedRep);
        targetUid = selectedContractor?.id || 'unassigned';
        
        targetName = selectedContractor 
          ? (selectedContractor.displayName || selectedContractor.name || `${selectedContractor.firstName || selectedContractor.first_name || ''} ${selectedContractor.lastName || selectedContractor.last_name || ''}`).trim() 
          : 'Unassigned';
      }

      const payload = {
        company_name: formData.entityName,
        contact_name: formData.contactName,
        contact_email: formData.contactEmail,
        contact_phone: formData.contactPhone,
        mrr_value: Number(formData.targetMrr),
        mrr: Number(formData.targetMrr), 
        notes: formData.notes,
        entity_role: formData.entityRole,
        industry: formData.industry,
        employee_count: formData.employeeCount,
        assigned_apps: formData.assignedApps,
        rep_uid: targetUid, 
        rep_name: targetName,
        stage: 'newLeads', // Injects straight to the left side of the Kanban
        created_at: new Date()
      };

      await createTenantLead(tenantId, payload);
      onClose(); 
    } catch (error) {
      console.error("Failed to add lead", error);
      alert("System Error: Could not dispatch lead to database.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-2xl bg-slate-50 h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right">
        
        {/* Header */}
        <div className="bg-slate-900 p-6 border-b border-slate-700 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-black text-white uppercase tracking-tight">
              {isOperatorView ? "Quick Lead Intake" : "Route Lead Intake"}
            </h2>
            <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mt-1">
              {isOperatorView ? "Local Pipeline Injection" : "Master Routing & Assignment"}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        {/* High-Density Form */}
        <form onSubmit={handleSubmit} id="lead-intake-form" className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
          
          {/* Admin Routing Dropdown */}
          {!isOperatorView && (
            <div className="bg-amber-50 border border-amber-200 p-4 shadow-sm rounded-sm">
              <label className="block text-[10px] font-bold text-amber-800 uppercase tracking-widest mb-2 flex items-center gap-2">
                <i className="fa-solid fa-route"></i> Master Lead Routing (Assign To Operator)
              </label>
              <select 
                value={assignedRep} 
                onChange={(e) => setAssignedRep(e.target.value)}
                className="w-full bg-white border border-amber-300 px-3 py-2 text-sm font-bold text-slate-900 focus:outline-none focus:border-amber-500 cursor-pointer rounded-sm"
              >
                {contractors.length === 0 ? (
                  <option value="">Loading Fleet...</option>
                ) : (
                  contractors.map(rep => {
                    const repName = (rep.displayName || rep.name || `${rep.firstName || rep.first_name || ''} ${rep.lastName || rep.last_name || ''}`).trim() || 'Unknown';
                    const repLocation = rep.operatingCity || rep.location || 'Corporate HQ';
                    
                    return (
                      <option key={rep.id} value={rep.id}>
                        {repName} ({repLocation})
                      </option>
                    );
                  })
                )}
              </select>
            </div>
          )}

          {/* Section 1: Entity Details */}
          <div>
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-300 pb-2 mb-4">Entity Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Legal Entity Name</label>
                <input required type="text" name="entityName" value={formData.entityName} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Entity Role / Classification</label>
                <select name="entityRole" value={formData.entityRole} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                  <option value="">Select Role...</option>
                  <option value="Authorized Retailer">Authorized Retailer</option>
                  <option value="Master Agent">Master Agent / Dealer</option>
                  <option value="Independent Operator">Independent Operator</option>
                  <option value="Direct Brand">Direct Brand</option>
                  <option value="Vendor / Partner">Vendor / Partner</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Industry / Vertical</label>
                <input type="text" name="industry" value={formData.industry} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Est. Headcount</label>
                <select name="employeeCount" value={formData.employeeCount} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                  <option value="">Select Range...</option>
                  <option value="1-10">1 - 10</option>
                  <option value="11-50">11 - 50</option>
                  <option value="51-250">51 - 250 (Enterprise)</option>
                  <option value="250+">250+ (Global)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Contact Information */}
          <div>
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-300 pb-2 mb-4">Primary Decision Maker</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Full Name</label>
                <input required type="text" name="contactName" value={formData.contactName} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate Title</label>
                <input required type="text" name="contactTitle" value={formData.contactTitle} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Direct Email (Used for Admin Handoff)</label>
                <input required type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Direct Phone</label>
                <input type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
            </div>
          </div>

          {/* Section 3: Financials & Provisioning */}
          <div>
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-300 pb-2 mb-4">Financial Target & Provisioning</h3>
            <div className="grid grid-cols-2 gap-6">
              
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Target MRR ($)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                  <input required type="number" name="targetMrr" value={formData.targetMrr} onChange={handleChange} className="w-full bg-white border border-slate-300 pl-8 pr-3 py-2 text-sm font-bold text-emerald-700 focus:border-emerald-500 focus:outline-none rounded-sm" placeholder="0.00" />
                </div>
              </div>
              
              <div className="col-span-2 bg-slate-50 p-5 border border-slate-200 shadow-inner rounded-sm">
                <label className="block text-[10px] font-black text-slate-800 uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">Target Service Matrix</label>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                  {SERVICE_OPTIONS.map((service) => (
                    <label key={service.id} className="flex items-start gap-3 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        name={service.id} 
                        checked={formData.assignedApps[service.id]} 
                        onChange={handleChange} 
                        className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300 rounded-sm cursor-pointer" 
                      />
                      <span className="text-xs font-bold text-slate-700 group-hover:text-slate-900 transition-colors">
                        {service.label}
                      </span>
                    </label>
                  ))}
                </div>

              </div>
            </div>
          </div>
        </form>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-300 bg-white flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-bold text-slate-600 border border-slate-300 hover:bg-slate-50 uppercase tracking-widest transition-colors rounded-sm">
            Cancel
          </button>
          <button 
            type="submit" 
            form="lead-intake-form"
            disabled={isSubmitting || tenantLoading} 
            className="px-6 py-2 text-[10px] font-bold text-white bg-emerald-600 border border-emerald-600 hover:bg-emerald-700 uppercase tracking-widest transition-colors shadow-sm rounded-sm disabled:opacity-50"
          >
            {tenantLoading ? 'Authenticating...' : (isSubmitting ? 'Transmitting...' : 'Save Record')}
          </button>
        </div>

      </div>
    </div>
  );
}